import type { Dispatch, SetStateAction } from 'react';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { useGenerationState } from '../state/useGenerationState';
import { AspectMismatchDialog } from './AspectMismatchDialog';
import { FrameMismatchDialog } from './FrameMismatchDialog';
import type { ScenarioConfirmDialogs } from './hooks/useScenarioConfirmDialogs';
import type { FrameMismatchConfirm } from './hooks/useVideoHandlers';

export interface ScenarioDialogsProps {
  /** useScenarioConfirmDialogs が持つ確認モーダル群 (state + ハンドラ)。 */
  dialogs: ScenarioConfirmDialogs;

  // useVideoHandlers 由来 (動画生成の直前確認)
  aspectMismatchConfirmOpen: boolean;
  onAspectMismatchConfirm: () => void;
  onAspectMismatchChangeAndConfirm: (target: '16:9' | '9:16') => void;
  onAspectMismatchCancel: () => void;
  noFrameConfirmOpen: boolean;
  noFrameConfirmDontShow: boolean;
  setNoFrameConfirmDontShow: Dispatch<SetStateAction<boolean>>;
  onNoFrameConfirm: () => void;
  onNoFrameCancel: () => void;
  frameMismatchConfirm: FrameMismatchConfirm | null;
  onFrameMismatchSelect: (frame: 'confirmed' | 'displayed') => void;
  onFrameMismatchConfirm: () => void;
  onFrameMismatchCancel: () => void;
}

/**
 * ScenarioGenerator 末尾にベタ書きされていた確認モーダル群をまとめたコンポーネント。
 * 描画順は元の JSX と同じ (DOM 構造を変えない)。「全フレーム削除」の確認だけは
 * WorkPanel 側にあるため、ここには含めない。
 */
export function ScenarioDialogs({
  dialogs,
  aspectMismatchConfirmOpen,
  onAspectMismatchConfirm,
  onAspectMismatchChangeAndConfirm,
  onAspectMismatchCancel,
  noFrameConfirmOpen,
  noFrameConfirmDontShow,
  setNoFrameConfirmDontShow,
  onNoFrameConfirm,
  onNoFrameCancel,
  frameMismatchConfirm,
  onFrameMismatchSelect,
  onFrameMismatchConfirm,
  onFrameMismatchCancel,
}: ScenarioDialogsProps) {
  const { videoSettings, selectedAssets } = useGenerationState();
  const { actorClearConfirm } = dialogs;

  return (
    <>
      {/* Seedance ↔ Veo 切替で俳優選択がクリアされる前の確認 */}
      <ConfirmDialog
        isOpen={!!actorClearConfirm}
        title="AI 俳優の選択がクリアされます"
        message={
          actorClearConfirm?.side === 'castingToSeedance' ? (
            (actorClearConfirm.keepCastActors && actorClearConfirm.keepCastActors.length > 0) ? (
              <>
                Seedance では、選択中の人間の AI 俳優は使用できません（キャラクター俳優は引き続き選択できます）。
                <br /><br />
                続行すると人間の俳優の選択のみクリアされます。よろしいですか？
              </>
            ) : (
              <>
                Seedance では、現在選択中の AI 俳優は使用できません。
                <br /><br />
                続行すると俳優選択がクリアされます。よろしいですか？
              </>
            )
          ) : (
            <>
              Veo では、現在選択中の Seedance 俳優は使用できません。
              <br /><br />
              続行すると俳優選択がクリアされます。よろしいですか？
            </>
          )
        }
        confirmLabel="続行"
        cancelLabel="キャンセル"
        variant="warning"
        buttonsAlign="space-between"
        onConfirm={dialogs.onActorClearConfirm}
        onCancel={dialogs.onActorClearCancel}
      />

      {/* Seedance 系を選んだ時、illustration=false なら「絵コンテをイラストで描く」を勧める案内 */}
      <ConfirmDialog
        isOpen={dialogs.showSeedanceFaceWarning}
        title="Seedance では人物の顔を直接扱えません"
        message={
          <>
            Seedance 2.0 系では、実在の人物の顔を含む素材を直接利用できない制約があります。
            <br /><br />
            「絵コンテをイラストで描く」モードを有効にすることをおすすめします。絵コンテを
            手書き風イラストで生成するため、参照画像に実在の顔写真が含まれず、完成動画は
            実写として生成されます。
            <br /><br />
            このまま続行すると、参考画像に顔が含まれる場合や登場人物がアップで描かれた
            場合に動画生成が失敗する可能性があります。
          </>
        }
        confirmLabel="絵コンテをイラストで描く"
        cancelLabel="そのまま続行"
        variant="warning"
        buttonsAlign="space-between"
        onConfirm={dialogs.onSeedanceFaceWarningConfirm}
        onCancel={dialogs.onSeedanceFaceWarningCancel}
      />

      {/* Veo を選んだ時、avoidFaces=true なら「顔を描かない」のオフを勧める案内。
          ⚠️ 「顔を描かない」は廃止済み機能 (トグル非表示)。旧 localStorage / シナリオ
          metadata に true が残っているユーザーを Off に誘導する脱出ハッチとして残置。 */}
      <ConfirmDialog
        isOpen={dialogs.showVeoFaceOffNotice}
        title="「顔を描かない」モードをOffにしますか？"
        message={
          <>
            Google Veoは実在の人物の顔を含む素材を多くの場合はそのまま扱えるため、「顔を描かない」モードは
            オフにすることをおすすめします。
          </>
        }
        confirmLabel="オフにする"
        cancelLabel="そのまま続行"
        variant="warning"
        buttonsAlign="space-between"
        onConfirm={dialogs.onVeoFaceOffNoticeConfirm}
        onCancel={dialogs.onVeoFaceOffNoticeCancel}
      />

      {/* 参考画像モードからフレームモードへの切替時、非対応素材がある場合の警告 */}
      <ConfirmDialog
        isOpen={dialogs.showKeepReferenceConfirm}
        title="動画生成方式の切り替え"
        message={<>現在選択されている素材は 16:9 もしくは 9:16 のアスペクト比ではないため、「{dialogs.keepReferenceModeLabel}」のままをおすすめします。<br /><br />「静止画フレームから動画化」の場合、上下または左右が大きくトリミングされる可能性があります。</>}
        confirmLabel={`「${dialogs.keepReferenceModeLabel}」を維持`}
        cancelLabel="「静止画フレームから動画化」に切替"
        variant="warning"
        buttonsAlign="space-between"
        onConfirm={dialogs.onKeepReferenceConfirm}
        onCancel={dialogs.onKeepReferenceCancel}
      />

      {/* フレーム非対応アスペクト比の素材が追加された時、参考画像モード切替を提案 */}
      <ConfirmDialog
        isOpen={dialogs.showSwitchToReferenceConfirm}
        title="動画生成方式の切り替え"
        message={<>16:9 または 9:16 以外のアスペクト比の素材が選択されました。<br /><br />「静止画フレームから動画化」の場合、上下もしくは左右が大きく切り取られた動画が生成される可能性があります。「AIおまかせ」に切り替えますか？</>}
        confirmLabel="「AIおまかせ」に切替"
        cancelLabel="「静止画フレームから動画化」を維持"
        variant="info"
        buttonsAlign="space-between"
        onConfirm={dialogs.onSwitchToReferenceConfirm}
        onCancel={dialogs.onSwitchToReferenceCancel}
      />

      {/* フレームモード: 選択素材のアスペクト比と動画設定が不一致の場合の確認（シミュレーション付き） */}
      <AspectMismatchDialog
        isOpen={aspectMismatchConfirmOpen}
        videoSettings={videoSettings}
        selectedAssets={selectedAssets}
        onConfirm={onAspectMismatchConfirm}
        onChangeAndConfirm={onAspectMismatchChangeAndConfirm}
        onCancel={onAspectMismatchCancel}
      />

      {/* フレーム未指定で動画生成する際の確認ダイアログ */}
      <ConfirmDialog
        isOpen={noFrameConfirmOpen}
        title="確認"
        message="最初のフレームを指定せずに動画を生成すると、ガチャ要素が大きくなります。よろしいですか？"
        confirmLabel="はい"
        cancelLabel="いいえ"
        variant="warning"
        onConfirm={onNoFrameConfirm}
        onCancel={onNoFrameCancel}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '12px 0 0', fontSize: '13px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={noFrameConfirmDontShow}
            onChange={(e) => setNoFrameConfirmDontShow(e.target.checked)}
          />
          今後はこのメッセージを表示しない
        </label>
      </ConfirmDialog>

      {/* フレーム不一致確認モーダル */}
      <FrameMismatchDialog
        state={frameMismatchConfirm}
        onSelect={onFrameMismatchSelect}
        onConfirm={onFrameMismatchConfirm}
        onCancel={onFrameMismatchCancel}
      />
    </>
  );
}
